import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import ComplianceScoreCard from './components/ComplianceScoreCard';

import ComplianceTrendChart from './components/ComplianceTrendChart';
import DepartmentComplianceTable from './components/DepartmentComplianceTable';

import AlertDashboardWidget from './components/AlertDashboardWidget';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import PayrollSyncDashboard from './components/PayrollSyncDashboard';
import DashboardCustomizationModal from './components/DashboardCustomizationModal';
import FilteredViewCreator from './components/FilteredViewCreator';

import ReportSchedulerModal from './components/ReportSchedulerModal';
import Button from '../../components/ui/Button';
import EmptyStatePrompt from '../../components/EmptyStatePrompt';
import { employeeService } from '../../services/employeeService';
import { complianceService } from '../../services/complianceService';
import { alertService } from '../../services/alertService';
import realtimeDashboardService from '../../services/realtimeDashboardService';
import Icon from '../../components/AppIcon';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';
import EmailNotificationBanner from './components/EmailNotificationBanner';
import PngNewsWidget from './components/PngNewsWidget';





const ComplianceOverview = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filters, setFilters] = useState({
    department: '',
    dateRange: { start: '', end: '' },
    employmentType: ''
  });
  const [showCustomizationModal, setShowCustomizationModal] = useState(false);
  const [showFilterCreator, setShowFilterCreator] = useState(false);
  const [showReportScheduler, setShowReportScheduler] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kpiMetrics, setKpiMetrics] = useState([]);
  const [complianceTrendData, setComplianceTrendData] = useState([]);
  const [violationAlerts, setViolationAlerts] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [regulationCategories, setRegulationCategories] = useState([
    { id: "all", name: "All Regulations", icon: "Shield", count: 0 },
    { id: "employment", name: "Employment Act", icon: "FileText", count: 0 },
    { id: "leave", name: "Leave Policies", icon: "Calendar", count: 0 },
    { id: "payroll", name: "Payroll & Tax", icon: "DollarSign", count: 0 },
    { id: "contracts", name: "Contracts", icon: "FileSignature", count: 0 }
  ]);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');
  const [recentNews, setRecentNews] = useState([]);

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  useEffect(() => {
    loadData();

    // Check subscription status
    checkSubscription();

    // Subscribe to real-time dashboard updates
    const channels = realtimeDashboardService?.subscribeToAllUpdates(
      {
        onViolation: (violation, eventType) => {
          console.log('Real-time violation update:', violation, eventType);
          loadData(); // Refresh dashboard data
        },
        onPayrollError: (error) => {
          console.log('Real-time payroll error:', error);
          loadData(); // Refresh dashboard data
        },
        onDeadline: (deadline, eventType) => {
          console.log('Real-time deadline update:', deadline, eventType);
          loadData(); // Refresh dashboard data
        }
      },
      filters?.department || 'all'
    );

    return () => {
      realtimeDashboardService?.unsubscribeAll(channels);
    };
  }, [filters?.department]);

  const loadData = async () => {
    try {
      setLoading(true);
      const employeesData = await employeeService?.getAll();
      setEmployees(employeesData || []);

      if (employeesData?.length > 0) {
        // Load compliance data only if employees exist
        const [metricsData, trendsData, alertsData, deptData] = await Promise.all([
          complianceService?.getComplianceMetrics(),
          complianceService?.getComplianceTrends(),
          alertService?.getActiveAlerts(),
          complianceService?.getDepartmentCompliance()
        ]);

        setKpiMetrics(metricsData || []);
        setComplianceTrendData(trendsData || []);
        setViolationAlerts(alertsData || []);
        setDepartmentData(deptData || []);
      }
    } catch (error) {
      console.error('Error loading compliance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const handleResolveViolation = (violationId) => {
    console.log('Resolving violation:', violationId);
  };

  const handleViewViolationDetails = (violationId) => {
    console.log('Viewing violation details:', violationId);
  };

  const handleExportReport = () => {
    console.log('Exporting compliance report');
  };

  const handleViewAudit = (departmentId) => {
    console.log('Viewing audit for department:', departmentId);
  };

  const handleApplyFilteredView = (filterCriteria) => {
    setAppliedFilters(filterCriteria);
  };

  const handleSaveCustomization = () => {
    window.location?.reload();
  };

  const handleSaveFilteredView = () => {
    setShowFilterCreator(false);
  };

  const handleSaveReportSchedule = () => {
    setShowReportScheduler(false);
  };

  const handleAddEmployees = () => {
    window.location.href = '/workforce-management-dashboard';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading compliance data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no employees
  if (!employees || employees?.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <Header />
        <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
              Compliance Overview Dashboard
            </h1>
            <p className="caption text-sm md:text-base text-muted-foreground">
              Real-time monitoring of PNG Employment Act 1978 adherence and regulatory compliance
            </p>
          </div>

          <div className="bg-card rounded-lg border border-border p-8">
            <EmptyStatePrompt
              icon="Users"
              title="No Employees Added Yet"
              description="Start building your workforce by adding employees. Once you add your first employee, compliance tracking, payroll monitoring, and regulatory reporting will be automatically activated."
              actionLabel="Add Your First Employee"
              onAction={handleAddEmployees}
            />
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-card rounded-lg border border-border p-6 text-center">
              <Icon name="Shield" className="w-8 h-8 text-muted-foreground mx-auto mb-3 flex-shrink-0" />
              <h3 className="font-semibold text-foreground mb-2 break-words">Compliance Tracking</h3>
              <p className="text-sm text-muted-foreground break-words">
                Monitor PNG Employment Act adherence automatically
              </p>
            </div>
            <div className="bg-card rounded-lg border border-border p-6 text-center">
              <Icon name="DollarSign" className="w-8 h-8 text-muted-foreground mx-auto mb-3 flex-shrink-0" />
              <h3 className="font-semibold text-foreground mb-2 break-words">Payroll Compliance</h3>
              <p className="text-sm text-muted-foreground break-words">
                Ensure accurate NASFUND and SWT calculations
              </p>
            </div>
            <div className="bg-card rounded-lg border border-border p-6 text-center">
              <Icon name="FileText" className="w-8 h-8 text-muted-foreground mx-auto mb-3 flex-shrink-0" />
              <h3 className="font-semibold text-foreground mb-2 break-words">Automated Reports</h3>
              <p className="text-sm text-muted-foreground break-words">
                Generate compliance reports for government filing
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <Header />
      <ComplianceAlertBanner />
      <EmailNotificationBanner />
      <GlobalFilterBar onFilterChange={handleFilterChange} />
      
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Compliance Overview</h1>
              <p className="text-gray-600 dark:text-gray-400">Monitor PNG employment law compliance and workforce regulations</p>
            </div>
            <div className="flex items-center gap-3">
              <DataRefreshIndicator />
              <Button
                variant="outline"
                iconName="Settings"
                onClick={() => setShowCustomizationModal(true)}
              >
                Customize
              </Button>
              <Button
                variant="default"
                iconName="Download"
                onClick={handleExportReport}
              >
                Export Report
              </Button>
            </div>
          </div>
        </div>

        {employees?.length === 0 ? (
          <EmptyStatePrompt
            icon="Users"
            title="No Employees Found"
            description="Add employees to your workforce to start tracking compliance metrics and generate reports."
            actionLabel="Add Employees"
            onAction={handleAddEmployees}
          />
        ) : (
          <>
            {/* KPI Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {kpiMetrics?.map((metric, index) => (
                <ComplianceScoreCard
                  key={index}
                  title={metric?.label}
                  value={metric?.value}
                  subtitle={metric?.subtitle}
                  status={metric?.status}
                  trend={metric?.trend}
                  icon={metric?.icon}
                />
              ))}
            </div>

            {/* Regulation Categories */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Regulation Categories</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {regulationCategories?.map((category) => (
                  <button
                    key={category?.id}
                    onClick={() => handleCategoryChange(category?.id)}
                    className={`p-4 rounded-lg border transition-all text-left ${
                      selectedCategory === category?.id
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-600' :'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-600'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Icon name={category?.icon} size={20} className={selectedCategory === category?.id ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-600 dark:text-gray-400'} />
                      <span className={`text-sm font-semibold ${selectedCategory === category?.id ? 'text-emerald-700 dark:text-emerald-400' : 'text-gray-900 dark:text-white'}`}>
                        {category?.name}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Compliance Trend Chart */}
            <div className="mb-8">
              <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Compliance Trends</h2>
                <ComplianceTrendChart data={complianceTrendData} selectedCategory={selectedCategory} />
              </div>
            </div>

            {/* Alerts and Department Compliance */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-1">
                <AlertDashboardWidget
                  alerts={violationAlerts}
                  onResolve={handleResolveViolation}
                  onViewDetails={handleViewViolationDetails}
                />
              </div>
              <div className="lg:col-span-2">
                <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Department Compliance</h2>
                  <DepartmentComplianceTable
                    departments={departmentData}
                    onViewAudit={handleViewAudit}
                    onExport={handleExportReport}
                  />
                </div>
              </div>
            </div>

            {/* PNG News Widget */}
            <div className="mb-8">
              <PngNewsWidget news={recentNews} />
            </div>

            {/* Payroll Sync Dashboard */}
            <div className="mb-8">
              <PayrollSyncDashboard />
            </div>
          </>
        )}
      </main>

      <LegalDisclaimerBanner />

      {/* Modals */}
      {showCustomizationModal && (
        <DashboardCustomizationModal
          onClose={() => setShowCustomizationModal(false)}
          onSave={handleSaveCustomization}
        />
      )}

      {showFilterCreator && (
        <FilteredViewCreator
          onClose={() => setShowFilterCreator(false)}
          onSave={handleSaveFilteredView}
          targetEntity="compliance"
        />
      )}

      {showReportScheduler && (
        <ReportSchedulerModal
          onClose={() => setShowReportScheduler(false)}
          onSave={handleSaveReportSchedule}
        />
      )}

      {showRedirectModal && (
        <TeaserEditModal
          field={teaserEditField}
          onClose={closeModal}
          isOpen={showRedirectModal}
        />
      )}
    </div>
  );
};

export default ComplianceOverview;